/**
 * InstallPrompt.tsx
 *
 * Displays a bottom banner inviting users to install AIP on their home screen.
 *
 * Behaviour by platform:
 *   Chrome / Edge / Android  — listens for the `beforeinstallprompt` event and
 *                              calls prompt() when the user clicks "Install AIP".
 *   iOS / iPadOS             — the browser never fires beforeinstallprompt, so we
 *                              detect the iOS user agent and show a manual hint.
 *   Already installed        — if display-mode is standalone, the banner is hidden.
 *   Dismissed                — stored in localStorage so the banner stays hidden.
 */

import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";

const DISMISSED_KEY = "aip_install_prompt_dismissed";
const SESSION_SHOWN_KEY = "aip_install_prompt_shown_this_session";

/** Returns true when running as an installed PWA (standalone or fullscreen). */
function isStandaloneMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

/** Detect iOS / iPadOS user agents. */
function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPad on iOS 13+ reports as "Macintosh" but has touch support
    (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

type InstallState =
  | "hidden"         // already installed or dismissed
  | "android-ready"  // beforeinstallprompt captured, ready to show native prompt
  | "ios-hint"       // iOS — show manual share sheet instructions
  | "checking";      // initial

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallPrompt() {
  const [state, setState] = useState<InstallState>("checking");
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [location] = useLocation();
  // W43.2: suppress install banner on /galaxy — it covers the Canvas at the bottom
  // of the viewport and eats touch/pinch events before OrbitControls sees them.
  const isGalaxy = location.startsWith("/galaxy");

  useEffect(() => {
    // Already installed — never show the banner
    if (isStandaloneMode()) {
      setState("hidden");
      return;
    }

    // Previously dismissed (persistent)
    if (localStorage.getItem(DISMISSED_KEY) === "1") {
      setState("hidden");
      return;
    }

    // D-003: throttle to once per session — do not nag on every page load
    if (sessionStorage.getItem(SESSION_SHOWN_KEY) === "1") {
      setState("hidden");
      return;
    }
    try { sessionStorage.setItem(SESSION_SHOWN_KEY, "1"); } catch { /* ignore */ }

    if (isIOS()) {
      // Only show the iOS hint if not already standalone
      if ((window.navigator as any).standalone !== true) {
        setState("ios-hint");
      } else {
        setState("hidden");
      }
      return;
    }

    // Chrome / Edge / Android: wait for the browser's install signal
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setState("android-ready");
    };
    window.addEventListener("beforeinstallprompt", handler);

    // If appinstalled fires, hide the banner
    const installed = () => setState("hidden");
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  function handleInstall() {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    deferredPrompt.current.userChoice.then((choice) => {
      if (choice.outcome === "accepted") {
        setState("hidden");
      }
      deferredPrompt.current = null;
    });
  }

  function handleDismiss() {
    localStorage.setItem(DISMISSED_KEY, "1");
    setState("hidden");
  }

  if (state === "hidden" || state === "checking") return null;
  // W43.2: Never show install banner on Galaxy — it blocks canvas touch events
  if (isGalaxy) return null;

  return (
    <div
      role="banner"
      aria-label="Install Attilius on your home screen"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: "#0B0B0F",
        color: "#FAFAF7",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        boxShadow: "0 -2px 12px rgba(0,0,0,0.35)",
        fontFamily: "'Inter', 'DM Sans', sans-serif",
        fontSize: "14px",
        lineHeight: "1.4",
      }}
    >
      {/* Left: icon + copy */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
        <img
          src="/pwa-icons/icon-192x192.png"
          alt="Attilius"
          width={36}
          height={36}
          style={{ borderRadius: "8px", flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: "#FAFAF7" }}>Install Attilius</div>
          {state === "ios-hint" ? (
            <div style={{ color: "#8E8E94", fontSize: "12px", marginTop: "2px" }}>
              Tap the Share button, then "Add to Home Screen"
            </div>
          ) : (
            <div style={{ color: "#8E8E94", fontSize: "12px", marginTop: "2px" }}>
              Add to your home screen for full-screen access
            </div>
          )}
        </div>
      </div>

      {/* Right: action buttons */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
        {state === "android-ready" && (
          <button
            onClick={handleInstall}
            style={{
              background: "#DC2626",
              color: "#FAFAF7",
              border: "none",
              borderRadius: "6px",
              padding: "7px 14px",
              fontWeight: 600,
              fontSize: "13px",
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Install
          </button>
        )}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          style={{
            background: "transparent",
            color: "#6B6B70",
            border: "1px solid #3F3F44",
            borderRadius: "6px",
            padding: "6px 10px",
            fontSize: "13px",
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
